import { format } from "date-fns";

import { getOrganizationCarbonEmissions } from "Api/carbonEmissions/getOrganizationCarbonEmissions.request";
import { createApiErrorPayload } from "src/common/utils/apiErrorHandling";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import { setOrganizationLoading } from "../carbonEmissions.reducer";
import { EMISSIONS_DATA_YEAR } from "../constants";
import { normalizeCarbonEmissionsData } from "../normalizeInput";

// Define a custom error payload type that includes organizationId
interface ErrorPayload {
  error: string;
  organizationId?: string;
  isNonRetryable?: boolean; // Flag for non-retryable errors like 404
}

// Helper function to format dates for API
const formatDate = (date: Date): string => format(date, "yyyy-MM-dd");

/**
 * Fetches carbon emissions data for the previous year by organization.
 *
 * This thunk retrieves monthly carbon emissions data for a specific organization,
 * covering the date range from January 1st of the previous year through January 1st
 * of the current year (to capture all of December in the previous year).
 *
 * @param organizationId - UUID of the organization to fetch emissions data for
 * @returns Promise resolving to normalized emissions data containing:
 *   - organizationId: The organization UUID
 *   - data: Cleaned emissions data with projects and their monthly values
 *
 * @throws {ErrorPayload} Rejects with error payload containing:
 *   - error: Human-readable error message
 *   - organizationId: The organization UUID
 *   - isNonRetryable: true for 400/401/403/404 errors, false otherwise
 *
 * Date Range Logic:
 * - Start: January 1st of previous year (e.g., 2023-01-01 when current year is 2024)
 * - End: January 1st of current year (e.g., 2024-01-01)
 * - Interval: Monthly aggregation
 *
 * Error Handling:
 * - Detects non-retryable errors (404, 403, 401, 400) to prevent retry loops
 * - Sets loading state before fetch and clears it on error
 * - Normalizes error responses from text or JSON formats
 *
 * @example
 * ```typescript
 * // In a component or hook
 * const dispatch = useAppDispatch();
 * const result = await dispatch(
 *   fetchPreviousYearMonthlyCarbonEmissionsThunk('org-uuid-123')
 * );
 *
 * if (fetchPreviousYearMonthlyCarbonEmissionsThunk.fulfilled.match(result)) {
 *   console.log('Emissions data:', result.payload.data);
 * }
 * ```
 */
export const fetchPreviousYearMonthlyCarbonEmissionsThunk = createAppAsyncThunk(
  "carbonEmissions/fetchPreviousYearMonthlyCarbonEmissions",
  async (organizationId: string, { rejectWithValue, dispatch }) => {
    try {
      // Set loading state using organizationId
      dispatch(
        setOrganizationLoading({
          organizationId,
          isLoading: true
        })
      );

      // Start date is Jan 1st of the emissions year
      const from = formatDate(new Date(EMISSIONS_DATA_YEAR, 0, 1));

      // End date is Jan 1st of the following year (to include all of December)
      const to = formatDate(new Date(EMISSIONS_DATA_YEAR + 1, 0, 1));

      const response = await getOrganizationCarbonEmissions({
        organizationId,
        from,
        to,
        interval: "month"
      });

      // Check if response has valid structure
      if (!response || !response.projects || response.projects.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("API returned empty or invalid carbon emissions data");
      }

      // Normalize the data with minimal transformation
      const normalizedData = normalizeCarbonEmissionsData(
        response,
        organizationId
      );

      // Return using organizationId for consistency
      return {
        organizationId,
        data: normalizedData
      };
    } catch (error) {
      // Set loading to false
      if (organizationId) {
        dispatch(
          setOrganizationLoading({
            organizationId,
            isLoading: false
          })
        );
      }

      const apiError = createApiErrorPayload(error);
      const errorPayload: ErrorPayload = {
        ...apiError,
        organizationId
      };

      return rejectWithValue(errorPayload);
    }
  }
);
